import React, { useEffect, useMemo, useState } from "react";
import type { TutorialAnimationPreset } from "../../data/tutorialInteractions";
import { ViewerSettings } from "../../types/settings";

type LanguageCode = ViewerSettings["language"];

type AnimationPhaseId =
  | "touchAppear"
  | "gestureMove"
  | "viewerResponse"
  | "feedback"
  | "reset";

interface AnimationPhase {
  id: AnimationPhaseId;
  durationMs: number;
}

interface TutorialInteractionAnimationProps {
  preset: TutorialAnimationPreset;
  accentColor?: string;
  language?: LanguageCode;
  isPlaying?: boolean;
  replayKey?: number;
  onReplay?: () => void;
}

function getAnimationUiCopy(language: LanguageCode) {
  switch (language) {
    case "es":
      return {
        replay: "Repetir animaci\u00f3n de interacci\u00f3n",
        clickToReplay: "Haz clic en la animaci\u00f3n para repetirla",
        selectionConfirmed: "Selecci\u00f3n confirmada",
        selectionDetails:
          "El visor resalta la pieza tocada y muestra detalles contextuales.",
        tap: "Tocar",
      };
    case "fr":
      return {
        replay: "Relancer l\u2019animation interactive",
        clickToReplay: "Cliquez sur l\u2019animation pour la relancer",
        selectionConfirmed: "S\u00e9lection confirm\u00e9e",
        selectionDetails:
          "Le visualiseur met en surbrillance la pi\u00e8ce touch\u00e9e et affiche des d\u00e9tails contextuels.",
        tap: "Toucher",
      };
    case "de":
      return {
        replay: "Interaktionsanimation wiederholen",
        clickToReplay: "Animation zum Wiederholen anklicken",
        selectionConfirmed: "Auswahl best\u00e4tigt",
        selectionDetails:
          "Der Viewer hebt das angetippte Teil hervor und zeigt kontextbezogene Details an.",
        tap: "Tippen",
      };
    case "zh":
      return {
        replay: "\u91cd\u64ad\u4ea4\u4e92\u52a8\u753b",
        clickToReplay: "\u70b9\u51fb\u52a8\u753b\u4ee5\u91cd\u64ad",
        selectionConfirmed: "\u9009\u62e9\u5df2\u786e\u8ba4",
        selectionDetails:
          "\u67e5\u770b\u5668\u4f1a\u9ad8\u4eae\u88ab\u70b9\u51fb\u7684\u90e8\u4ef6\uff0c\u5e76\u663e\u793a\u4e0a\u4e0b\u6587\u8be6\u60c5\u3002",
        tap: "\u70b9\u51fb",
      };
    case "en":
    default:
      return {
        replay: "Replay interaction animation",
        clickToReplay: "Click animation to replay",
        selectionConfirmed: "Selection Confirmed",
        selectionDetails:
          "The viewer highlights the tapped part and reveals contextual details.",
        tap: "Tap",
      };
  }
}

const TIMELINES: Record<TutorialAnimationPreset, AnimationPhase[]> = {
  "pinch-zoom": [
    { id: "touchAppear", durationMs: 600 },
    { id: "gestureMove", durationMs: 1100 },
    { id: "viewerResponse", durationMs: 900 },
    { id: "feedback", durationMs: 900 },
    { id: "reset", durationMs: 700 },
  ],

  "pan-object": [
    { id: "touchAppear", durationMs: 600 },
    { id: "gestureMove", durationMs: 1200 },
    { id: "viewerResponse", durationMs: 900 },
    { id: "feedback", durationMs: 800 },
    { id: "reset", durationMs: 700 },
  ],

  "tap-highlight": [
    { id: "touchAppear", durationMs: 500 },
    { id: "gestureMove", durationMs: 700 },
    { id: "viewerResponse", durationMs: 850 },
    { id: "feedback", durationMs: 1100 },
    { id: "reset", durationMs: 800 },
  ],

  "rotate-object": [
    { id: "touchAppear", durationMs: 600 },
    { id: "gestureMove", durationMs: 1100 },
    { id: "viewerResponse", durationMs: 900 },
    { id: "feedback", durationMs: 850 },
    { id: "reset", durationMs: 700 },
  ],

  custom: [
    { id: "touchAppear", durationMs: 500 },
    { id: "gestureMove", durationMs: 800 },
    { id: "viewerResponse", durationMs: 800 },
    { id: "feedback", durationMs: 800 },
    { id: "reset", durationMs: 700 },
  ],
};

const LABELS: Record<TutorialAnimationPreset, string[]> = {
  "pinch-zoom": [
    "Place two fingers",
    "Move fingers apart",
    "Viewer zooms smoothly",
    "Precise zoom achieved",
  ],

  "pan-object": [
    "Touch with two fingers",
    "Drag in one direction",
    "Viewer shifts position",
    "Model is re-centered",
  ],

  "tap-highlight": [
    "Aim at a visible part",
    "Tap once",
    "Part highlights and expands",
    "Information becomes available",
  ],

  "rotate-object": [
    "Touch with one finger",
    "Drag along the orbit path",
    "Model rotates around center",
    "New angle revealed",
  ],

  custom: [
    "Observe gesture",
    "Perform gesture",
    "Watch the response",
    "Interaction complete",
  ],
};

function getStageLabels(
  language: LanguageCode,
  preset: TutorialAnimationPreset,
) {
  if (language === "es") {
    return {
      "pinch-zoom": [
        "Coloca dos dedos",
        "Sep\u00e1ralos",
        "El visor hace zoom suavemente",
        "Zoom preciso conseguido",
      ],

      "pan-object": [
        "Toca con dos dedos",
        "Arrastra en una direcci\u00f3n",
        "El visor cambia de posici\u00f3n",
        "El modelo se recentra",
      ],

      "tap-highlight": [
        "Apunta a una parte visible",
        "Toca una vez",
        "La pieza se resalta y se expande",
        "La informaci\u00f3n queda disponible",
      ],

      "rotate-object": [
        "Toca con un dedo",
        "Arrastra por la trayectoria",
        "El modelo gira alrededor del centro",
        "Se revela un nuevo \u00e1ngulo",
      ],

      custom: [
        "Observa el gesto",
        "Realiza el gesto",
        "Observa la respuesta",
        "Interacci\u00f3n completada",
      ],
    }[preset];
  }

  if (language === "fr") {
    return {
      "pinch-zoom": [
        "Posez deux doigts",
        "\u00c9cartez les doigts",
        "Le visualiseur zoome en douceur",
        "Zoom pr\u00e9cis obtenu",
      ],

      "pan-object": [
        "Touchez avec deux doigts",
        "Faites glisser dans une direction",
        "Le visualiseur se d\u00e9place",
        "Le mod\u00e8le est recentr\u00e9",
      ],

      "tap-highlight": [
        "Visez une pi\u00e8ce visible",
        "Touchez une fois",
        "La pi\u00e8ce est mise en valeur",
        "Les informations deviennent disponibles",
      ],

      "rotate-object": [
        "Touchez avec un doigt",
        "Glissez le long de l\u2019orbite",
        "Le mod\u00e8le tourne autour du centre",
        "Un nouvel angle appara\u00eet",
      ],

      custom: [
        "Observez le geste",
        "Ex\u00e9cutez le geste",
        "Observez la r\u00e9ponse",
        "Interaction termin\u00e9e",
      ],
    }[preset];
  }

  if (language === "de") {
    return {
      "pinch-zoom": [
        "Zwei Finger auflegen",
        "Finger auseinanderziehen",
        "Der Viewer zoomt sanft",
        "Pr\u00e4ziser Zoom erreicht",
      ],

      "pan-object": [
        "Mit zwei Fingern ber\u00fchren",
        "In eine Richtung ziehen",
        "Der Viewer verschiebt sich",
        "Das Modell ist neu zentriert",
      ],

      "tap-highlight": [
        "Auf ein sichtbares Teil zielen",
        "Einmal tippen",
        "Teil wird hervorgehoben",
        "Informationen werden verf\u00fcgbar",
      ],

      "rotate-object": [
        "Mit einem Finger ber\u00fchren",
        "Entlang der Orbitbahn ziehen",
        "Das Modell rotiert um die Mitte",
        "Ein neuer Winkel wird sichtbar",
      ],

      custom: [
        "Geste beobachten",
        "Geste ausf\u00fchren",
        "Reaktion beobachten",
        "Interaktion abgeschlossen",
      ],
    }[preset];
  }

  if (language === "zh") {
    return {
      "pinch-zoom": [
        "\u653e\u4e0b\u4e24\u6839\u624b\u6307",
        "\u5c06\u624b\u6307\u5411\u5916\u79fb\u52a8",
        "\u67e5\u770b\u5668\u5e73\u7a33\u7f29\u653e",
        "\u5df2\u5b8c\u6210\u7cbe\u786e\u7f29\u653e",
      ],

      "pan-object": [
        "\u7528\u4e24\u6839\u624b\u6307\u89e6\u6478",
        "\u5411\u5355\u4e00\u65b9\u5411\u62d6\u52a8",
        "\u67e5\u770b\u5668\u5f00\u59cb\u79fb\u52a8",
        "\u6a21\u578b\u5df2\u91cd\u65b0\u5c45\u4e2d",
      ],

      "tap-highlight": [
        "\u5bf9\u51c6\u53ef\u89c1\u90e8\u4ef6",
        "\u70b9\u51fb\u4e00\u6b21",
        "\u90e8\u4ef6\u88ab\u9ad8\u4eae\u5e76\u5c55\u5f00",
        "\u76f8\u5173\u4fe1\u606f\u5df2\u53ef\u67e5\u770b",
      ],

      "rotate-object": [
        "\u7528\u4e00\u6839\u624b\u6307\u89e6\u6478",
        "\u6cbf\u8f68\u9053\u62d6\u52a8",
        "\u6a21\u578b\u7ed5\u4e2d\u5fc3\u65cb\u8f6c",
        "\u65b0\u89d2\u5ea6\u5df2\u663e\u793a",
      ],

      custom: [
        "\u89c2\u5bdf\u624b\u52bf",
        "\u6267\u884c\u624b\u52bf",
        "\u89c2\u5bdf\u54cd\u5e94",
        "\u4ea4\u4e92\u5b8c\u6210",
      ],
    }[preset];
  }

  return LABELS[preset] ?? LABELS.custom;
}

function getResponseLabel(
  language: LanguageCode,
  preset: TutorialAnimationPreset,
  active: boolean,
) {
  if (language === "es") {
    if (preset === "pinch-zoom") return active ? "Zoom +18 %" : "Zoom listo";
    if (preset === "pan-object")
      return active ? "Vista reposicionada" : "Desplazamiento listo";
    if (preset === "tap-highlight")
      return active ? "Pieza seleccionada" : "Selecci\u00f3n lista";
    if (preset === "rotate-object")
      return active ? "\u00d3rbita actualizada" : "Rotaci\u00f3n lista";
    return "Interacci\u00f3n lista";
  }

  if (language === "fr") {
    if (preset === "pinch-zoom")
      return active ? "Zoom +18 %" : "Zoom pr\u00eat";
    if (preset === "pan-object")
      return active ? "Vue repositionn\u00e9e" : "D\u00e9placement pr\u00eat";
    if (preset === "tap-highlight")
      return active
        ? "Pi\u00e8ce s\u00e9lectionn\u00e9e"
        : "S\u00e9lection pr\u00eate";
    if (preset === "rotate-object")
      return active ? "Orbite mise \u00e0 jour" : "Rotation pr\u00eate";
    return "Interaction pr\u00eate";
  }

  if (language === "de") {
    if (preset === "pinch-zoom") return active ? "Zoom +18 %" : "Zoom bereit";
    if (preset === "pan-object")
      return active ? "Ansicht neu positioniert" : "Verschieben bereit";
    if (preset === "tap-highlight")
      return active ? "Teil ausgew\u00e4hlt" : "Auswahl bereit";
    if (preset === "rotate-object")
      return active ? "Orbit aktualisiert" : "Drehung bereit";
    return "Interaktion bereit";
  }

  if (language === "zh") {
    if (preset === "pinch-zoom")
      return active ? "\u7f29\u653e +18%" : "\u7f29\u653e\u5df2\u5c31\u7eea";
    if (preset === "pan-object")
      return active
        ? "\u89c6\u56fe\u5df2\u91cd\u65b0\u5b9a\u4f4d"
        : "\u5e73\u79fb\u5df2\u5c31\u7eea";
    if (preset === "tap-highlight")
      return active
        ? "\u90e8\u4ef6\u5df2\u9009\u4e2d"
        : "\u9009\u62e9\u5df2\u5c31\u7eea";
    if (preset === "rotate-object")
      return active
        ? "\u8f68\u9053\u5df2\u66f4\u65b0"
        : "\u65cb\u8f6c\u5df2\u5c31\u7eea";
    return "\u4ea4\u4e92\u5df2\u5c31\u7eea";
  }

  if (preset === "pinch-zoom") return active ? "Zoom +18%" : "Zoom Ready";
  if (preset === "pan-object")
    return active ? "Viewport Repositioned" : "Pan Ready";
  if (preset === "tap-highlight")
    return active ? "Part Selected" : "Selection Ready";
  if (preset === "rotate-object")
    return active ? "Orbit Updated" : "Rotate Ready";
  return "Interaction Ready";
}

function usePrefersReducedMotion() {
  const [prefersReducedMotion, setPrefersReducedMotion] = useState(false);

  useEffect(() => {
    const mediaQuery = window.matchMedia("(prefers-reduced-motion: reduce)");
    const update = () => setPrefersReducedMotion(mediaQuery.matches);
    update();
    mediaQuery.addEventListener("change", update);
    return () => mediaQuery.removeEventListener("change", update);
  }, []);

  return prefersReducedMotion;
}

export function TutorialInteractionAnimation({
  preset,
  accentColor = "rgb(56, 189, 248)",
  language = "en",
  isPlaying = true,
  replayKey = 0,
  onReplay,
}: TutorialInteractionAnimationProps) {
  const uiCopy = getAnimationUiCopy(language);
  const prefersReducedMotion = usePrefersReducedMotion();
  const timeline = TIMELINES[preset] ?? TIMELINES.custom;
  const labels = getStageLabels(language, preset);
  const [phaseIndex, setPhaseIndex] = useState(0);

  useEffect(() => {
    setPhaseIndex(0);
  }, [preset, replayKey]);

  useEffect(() => {
    if (!isPlaying) return;
    if (prefersReducedMotion) return;

    const current = timeline[phaseIndex];
    const timeout = window.setTimeout(() => {
      setPhaseIndex((previous) => (previous + 1) % timeline.length);
    }, current.durationMs);

    return () => window.clearTimeout(timeout);
  }, [phaseIndex, isPlaying, prefersReducedMotion, timeline]);

  const phaseId = prefersReducedMotion
    ? "feedback"
    : (timeline[phaseIndex]?.id ?? "touchAppear");

  const stageLabel = useMemo(() => {
    switch (phaseId) {
      case "touchAppear":
        return labels[0];
      case "gestureMove":
        return labels[1];
      case "viewerResponse":
        return labels[2];
      case "feedback":
      case "reset":
      default:
        return labels[3];
    }
  }, [labels, phaseId]);

  const baseObjectScale =
    preset === "pinch-zoom" &&
    (phaseId === "viewerResponse" || phaseId === "feedback")
      ? 1.18
      : 1;
  const baseObjectTranslateX =
    preset === "pan-object" &&
    (phaseId === "gestureMove" || phaseId === "viewerResponse")
      ? 28
      : 0;
  const baseObjectRotate =
    preset === "rotate-object" &&
    (phaseId === "viewerResponse" || phaseId === "feedback")
      ? -22
      : 0;
  const infoPanelVisible =
    preset === "tap-highlight" &&
    (phaseId === "viewerResponse" || phaseId === "feedback");

  const touchOne = (() => {
    if (preset === "pinch-zoom") {
      return phaseId === "gestureMove" || phaseId === "viewerResponse"
        ? { left: "30%", top: "48%" }
        : { left: "42%", top: "48%" };
    }
    if (preset === "pan-object") {
      return phaseId === "gestureMove" || phaseId === "viewerResponse"
        ? { left: "40%", top: "46%" }
        : { left: "28%", top: "46%" };
    }
    if (preset === "tap-highlight") {
      return phaseId === "gestureMove" || phaseId === "viewerResponse"
        ? { left: "62%", top: "43%" }
        : { left: "68%", top: "67%" };
    }
    if (preset === "rotate-object") {
      return phaseId === "gestureMove" || phaseId === "viewerResponse"
        ? { left: "35%", top: "52%" }
        : { left: "60%", top: "52%" };
    }
    return { left: "50%", top: "50%" };
  })();

  const touchTwo = (() => {
    if (preset === "pinch-zoom") {
      return phaseId === "gestureMove" || phaseId === "viewerResponse"
        ? { left: "70%", top: "52%" }
        : { left: "58%", top: "52%" };
    }
    if (preset === "pan-object") {
      return phaseId === "gestureMove" || phaseId === "viewerResponse"
        ? { left: "54%", top: "54%" }
        : { left: "42%", top: "54%" };
    }
    return null;
  })();

  const pathHint = (() => {
    if (preset === "pinch-zoom") return "↔";
    if (preset === "pan-object") return "→";
    if (preset === "tap-highlight") return uiCopy.tap;
    if (preset === "rotate-object") return "↺";
    return "•";
  })();

  const responseLabel = (() => {
    if (preset === "pinch-zoom")
      return getResponseLabel(
        language,
        preset,
        phaseId === "viewerResponse" || phaseId === "feedback",
      );
    if (preset === "pan-object")
      return getResponseLabel(
        language,
        preset,
        phaseId === "viewerResponse" || phaseId === "feedback",
      );
    if (preset === "tap-highlight")
      return getResponseLabel(
        language,
        preset,
        phaseId === "viewerResponse" || phaseId === "feedback",
      );
    if (preset === "rotate-object")
      return getResponseLabel(
        language,
        preset,
        phaseId === "viewerResponse" || phaseId === "feedback",
      );
    return getResponseLabel(language, preset, false);
  })();

  const safePathHint =
    preset === "pinch-zoom"
      ? String.fromCharCode(0x2194)
      : preset === "pan-object"
        ? String.fromCharCode(0x2192)
        : preset === "tap-highlight"
          ? uiCopy.tap
          : preset === "rotate-object"
            ? String.fromCharCode(0x21ba)
            : String.fromCharCode(0x2022);

  return (
    <div
      onClick={onReplay}
      role="button"
      tabIndex={0}
      onKeyDown={(event) => {
        if (event.key === "Enter" || event.key === " ") {
          event.preventDefault();
          onReplay?.();
        }
      }}
      aria-label={uiCopy.replay}
      style={{
        width: "100%",
        minHeight: "280px",
        borderRadius: "1.25rem",
        background:
          "linear-gradient(180deg, rgba(255,255,255,0.045) 0%, rgba(255,255,255,0.02) 100%)",
        border: "1px solid rgba(255,255,255,0.06)",
        padding: "1.25rem",
        position: "relative",
        overflow: "hidden",
        cursor: "pointer",
        outline: "none",
      }}
      data-oid="r0ug9t5"
    >
      <style data-oid="y6l4ef5">{`
        @keyframes tutorialPulse {
          0% { transform: scale(0.9); opacity: 0.25; }
          70% { transform: scale(1.5); opacity: 0; }
          100% { transform: scale(1.5); opacity: 0; }
        }
        @keyframes tutorialShimmer {
          0% { transform: translateX(-120%); }
          100% { transform: translateX(120%); }
        }
        @keyframes tutorialFloat {
          0%, 100% { transform: translateY(0px); }
          50% { transform: translateY(-6px); }
        }
        @keyframes tutorialGridDrift {
          0% { transform: translate3d(0, 0, 0); }
          50% { transform: translate3d(-8px, 6px, 0); }
          100% { transform: translate3d(0, 0, 0); }
        }
      `}</style>

      <div
        style={{
          position: "absolute",
          inset: "1rem",
          borderRadius: "1rem",
          border: "1px solid rgba(255,255,255,0.05)",
          background:
            "radial-gradient(circle at center, rgba(56,189,248,0.08) 0%, rgba(255,255,255,0.015) 65%, rgba(255,255,255,0.01) 100%)",
        }}
        data-oid="mb.cgr2"
      />

      <div
        style={{
          position: "absolute",
          inset: "1.25rem",
          borderRadius: "1rem",
          overflow: "hidden",
        }}
        data-oid="_axm93-"
      >
        <div
          style={{
            position: "absolute",
            top: 0,
            bottom: 0,
            width: "30%",
            background:
              "linear-gradient(90deg, transparent 0%, rgba(255,255,255,0.05) 50%, transparent 100%)",
            animation: prefersReducedMotion
              ? "none"
              : "tutorialShimmer 3.8s linear infinite",
            opacity: 0.5,
          }}
          data-oid="6.7ur7."
        />
      </div>

      <div
        style={{
          position: "relative",
          zIndex: 1,
          height: "240px",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
        }}
        data-oid="3su:oy0"
      >
        <div
          style={{
            position: "absolute",
            width: "74%",
            height: "78%",
            borderRadius: "1rem",
            border: "1px solid rgba(255,255,255,0.08)",
            background: "rgba(8, 12, 18, 0.55)",
            boxShadow: `0 0 30px ${accentColor}1A inset`,
          }}
          data-oid="6_1om-k"
        />

        <div
          style={{
            position: "absolute",
            width: "74%",
            height: "78%",
            borderRadius: "1rem",
            overflow: "hidden",
          }}
          data-oid="h:6_81a"
        >
          <div
            style={{
              position: "absolute",
              inset: 0,
              opacity: 0.16,
              backgroundImage:
                "linear-gradient(rgba(255,255,255,0.18) 1px, transparent 1px), linear-gradient(90deg, rgba(255,255,255,0.18) 1px, transparent 1px)",
              backgroundSize: "34px 34px",
              animation: prefersReducedMotion
                ? "none"
                : "tutorialGridDrift 4.2s ease-in-out infinite",
            }}
            data-oid="-dls:g5"
          />

          <div
            style={{
              position: "absolute",
              inset: "14px",
              borderRadius: "0.85rem",
              border: "1px solid rgba(255,255,255,0.06)",
            }}
            data-oid="x4hq.hx"
          />

          <HudCorner
            position="top-left"
            accentColor={accentColor}
            data-oid="y2sqz5h"
          />

          <HudCorner
            position="top-right"
            accentColor={accentColor}
            data-oid="v-hu0.9"
          />

          <HudCorner
            position="bottom-left"
            accentColor={accentColor}
            data-oid="bbmnc7r"
          />

          <HudCorner
            position="bottom-right"
            accentColor={accentColor}
            data-oid="slaqjs5"
          />
        </div>

        <div
          style={{
            position: "absolute",
            top: "1.1rem",
            right: "2.3rem",
            display: "inline-flex",
            alignItems: "center",
            gap: "0.45rem",
            padding: "0.45rem 0.7rem",
            borderRadius: "999px",
            background: "rgba(8, 12, 18, 0.74)",
            border: `1px solid ${accentColor}26`,
            color: "rgba(255,255,255,0.92)",
            fontSize: "0.68rem",
            fontWeight: 700,
            boxShadow: `0 0 16px ${accentColor}1F`,
          }}
          data-oid="9iykfzl"
        >
          <span
            className="material-symbols-outlined"
            style={{ fontSize: "0.9rem", color: accentColor }}
            data-oid="2cf.p4q"
          >
            {preset === "tap-highlight"
              ? "touch_app"
              : preset === "rotate-object"
                ? "3d_rotation"
                : preset === "pan-object"
                  ? "open_with"
                  : "pinch"}
          </span>
          {responseLabel}
        </div>

        {preset === "rotate-object" && (
          <div
            style={{
              position: "absolute",
              width: "58%",
              height: "58%",
              borderRadius: "50%",
              border: `1px dashed ${accentColor}4D`,
              opacity:
                phaseId === "viewerResponse" || phaseId === "feedback"
                  ? 0.85
                  : 0.35,
              transition: "opacity 400ms ease",
            }}
            data-oid="y_7._jj"
          />
        )}

        <GenericModelProxy
          accentColor={accentColor}
          preset={preset}
          phaseId={phaseId}
          uiCopy={uiCopy}
          translateX={baseObjectTranslateX}
          scale={baseObjectScale}
          rotate={baseObjectRotate}
          infoPanelVisible={infoPanelVisible}
          prefersReducedMotion={prefersReducedMotion}
          data-oid="k_scc_p"
        />

        {(phaseId === "gestureMove" ||
          phaseId === "viewerResponse" ||
          phaseId === "feedback") && (
          <div
            style={{
              position: "absolute",
              top: "1.2rem",
              padding: "0.45rem 0.75rem",
              borderRadius: "999px",
              background: "rgba(8, 12, 18, 0.75)",
              border: `1px solid ${accentColor}33`,
              color: "rgba(255,255,255,0.9)",
              fontSize: "0.72rem",
              fontWeight: 600,
              boxShadow: `0 0 18px ${accentColor}26`,
            }}
            data-oid="pp1h:dj"
          >
            {stageLabel}
          </div>
        )}

        {(preset === "pinch-zoom" || preset === "pan-object") && touchTwo && (
          <div
            style={{
              position: "absolute",
              left: touchOne.left,
              top: touchOne.top,
              width: `calc(${touchTwo.left} - ${touchOne.left})`,
              height: "2px",
              transform: "translateY(-50%)",
              transformOrigin: "left center",
              background: `linear-gradient(90deg, ${accentColor}AA 0%, ${accentColor}22 100%)`,
              opacity:
                phaseId === "gestureMove" || phaseId === "viewerResponse"
                  ? 0.75
                  : 0,
              transition: "opacity 400ms ease",
              pointerEvents: "none",
            }}
            data-oid="htirbj8"
          />
        )}

        <div
          style={{
            position: "absolute",
            left: touchOne.left,
            top: touchOne.top,
            transform: "translate(-50%, -50%)",
            transition: "all 700ms ease",
            opacity:
              phaseId === "touchAppear" ||
              phaseId === "gestureMove" ||
              phaseId === "viewerResponse" ||
              phaseId === "feedback"
                ? 1
                : 0,
          }}
          data-oid=".yu3qg2"
        >
          <TouchPoint
            accentColor={accentColor}
            active={phaseId !== "reset"}
            showPulse={phaseId === "touchAppear" || phaseId === "gestureMove"}
            data-oid="rosxglw"
          />
        </div>

        {touchTwo && (
          <div
            style={{
              position: "absolute",
              left: touchTwo.left,
              top: touchTwo.top,
              transform: "translate(-50%, -50%)",
              transition: "all 700ms ease",
              opacity:
                phaseId === "touchAppear" ||
                phaseId === "gestureMove" ||
                phaseId === "viewerResponse" ||
                phaseId === "feedback"
                  ? 1
                  : 0,
            }}
            data-oid="qmffs9j"
          >
            <TouchPoint
              accentColor={accentColor}
              active={phaseId !== "reset"}
              showPulse={phaseId === "touchAppear" || phaseId === "gestureMove"}
              data-oid="h1:m5p0"
            />
          </div>
        )}

        <div
          style={{
            position: "absolute",
            bottom: "0.9rem",
            right: "1rem",
            display: "flex",
            alignItems: "center",
            gap: "0.45rem",
            color: "rgba(255,255,255,0.6)",
            fontSize: "0.7rem",
            background: "rgba(8, 12, 18, 0.55)",
            borderRadius: "999px",
            border: "1px solid rgba(255,255,255,0.08)",
            padding: "0.35rem 0.65rem",
            pointerEvents: "none",
          }}
          data-oid="r4rabul"
        >
          <span
            className="material-symbols-outlined"
            style={{ fontSize: "0.95rem" }}
            data-oid="ryrdjte"
          >
            replay
          </span>
          {uiCopy.clickToReplay}
        </div>

        <div
          style={{
            position: "absolute",
            left: "50%",
            top: preset === "tap-highlight" ? "35%" : "50%",
            transform: `translate(-50%, -50%) ${preset === "rotate-object" ? "rotate(-8deg)" : ""}`,
            color: `${accentColor}`,
            opacity:
              phaseId === "gestureMove" || phaseId === "viewerResponse"
                ? 0.75
                : 0.18,
            fontSize: preset === "tap-highlight" ? "0.95rem" : "1.8rem",
            fontWeight: 700,
            transition: "all 400ms ease",
            pointerEvents: "none",
          }}
          data-oid="21:ecl_"
        >
          {safePathHint}
        </div>
      </div>
    </div>
  );
}

function HudCorner({
  position,
  accentColor,
}: {
  position: "top-left" | "top-right" | "bottom-left" | "bottom-right";
  accentColor: string;
}) {
  const isTop = position.includes("top");
  const isLeft = position.includes("left");

  return (
    <div
      style={{
        position: "absolute",
        top: isTop ? "10px" : "auto",
        bottom: !isTop ? "10px" : "auto",
        left: isLeft ? "10px" : "auto",
        right: !isLeft ? "10px" : "auto",
        width: "24px",
        height: "24px",
        borderTop: isTop ? `2px solid ${accentColor}66` : "none",
        borderBottom: !isTop ? `2px solid ${accentColor}66` : "none",
        borderLeft: isLeft ? `2px solid ${accentColor}66` : "none",
        borderRight: !isLeft ? `2px solid ${accentColor}66` : "none",
        borderRadius: "2px",
        opacity: 0.8,
      }}
      data-oid=":d38neb"
    />
  );
}

function GenericModelProxy({
  accentColor,
  preset,
  phaseId,
  uiCopy,
  translateX,
  scale,
  rotate,
  infoPanelVisible,
  prefersReducedMotion,
}: {
  accentColor: string;
  preset: TutorialAnimationPreset;
  phaseId: AnimationPhaseId;
  uiCopy: ReturnType<typeof getAnimationUiCopy>;
  translateX: number;
  scale: number;
  rotate: number;
  infoPanelVisible: boolean;
  prefersReducedMotion: boolean;
}) {
  const highlightVisible =
    phaseId === "viewerResponse" || phaseId === "feedback";

  return (
    <div
      style={{
        position: "relative",
        width: "240px",
        height: "180px",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        transform: `translateX(${translateX}px) scale(${scale}) rotate(${rotate}deg)`,
        transition: "transform 850ms ease",
        animation: prefersReducedMotion
          ? "none"
          : "tutorialFloat 3s ease-in-out infinite",
      }}
      data-oid="1h5mx9i"
    >
      <div
        style={{
          position: "absolute",
          bottom: "26px",
          width: "142px",
          height: "30px",
          borderRadius: "50%",
          background:
            "radial-gradient(circle, rgba(0,0,0,0.45) 0%, rgba(0,0,0,0.08) 70%, transparent 100%)",
          filter: "blur(6px)",
          transform: "translateY(14px)",
        }}
        data-oid="tde:7d-"
      />

      {highlightVisible && preset === "tap-highlight" && (
        <div
          style={{
            position: "absolute",
            right: "42px",
            top: "54px",
            width: "34px",
            height: "34px",
            borderRadius: "50%",
            border: `2px solid ${accentColor}`,
            boxShadow: `0 0 18px ${accentColor}`,
            opacity: 0.9,
          }}
          data-oid="vooww6q"
        />
      )}

      <div
        style={{
          position: "absolute",
          width: "160px",
          height: "160px",
        }}
        data-oid="yfig2n1"
      >
        {[
          { left: "18px", top: "44px", rotate: "-22deg" },
          { right: "18px", top: "44px", rotate: "22deg" },
          { left: "28px", bottom: "30px", rotate: "18deg" },
          { right: "28px", bottom: "30px", rotate: "-18deg" },
        ].map((arm, index) => (
          <div
            key={index}
            style={{
              position: "absolute",
              width: "52px",
              height: "10px",
              borderRadius: "999px",
              background:
                "linear-gradient(90deg, rgba(255,255,255,0.12) 0%, rgba(56,189,248,0.22) 100%)",
              border: "1px solid rgba(255,255,255,0.08)",
              left: arm.left,
              right: arm.right,
              top: arm.top,
              bottom: arm.bottom,
              transform: `rotate(${arm.rotate})`,
              transformOrigin: "center",
            }}
            data-oid="a36vwbd"
          />
        ))}

        {[
          { left: "0px", top: "34px" },
          { right: "0px", top: "34px" },
          { left: "8px", bottom: "12px" },
          { right: "8px", bottom: "12px" },
        ].map((pod, index) => (
          <div
            key={index}
            style={{
              position: "absolute",
              width: "26px",
              height: "26px",
              borderRadius: "50%",
              background:
                "radial-gradient(circle at 35% 35%, rgba(255,255,255,0.22) 0%, rgba(56,189,248,0.16) 45%, rgba(8,12,18,0.92) 100%)",
              border: "1px solid rgba(255,255,255,0.12)",
              boxShadow: highlightVisible
                ? `0 0 12px ${accentColor}2E`
                : "none",
              left: pod.left,
              right: pod.right,
              top: pod.top,
              bottom: pod.bottom,
            }}
            data-oid="8:hq6cl"
          />
        ))}
      </div>

      <div
        style={{
          position: "relative",
          width: "118px",
          height: "86px",
          borderRadius: "1.35rem",
          background:
            "linear-gradient(160deg, rgba(255,255,255,0.18) 0%, rgba(56,189,248,0.20) 30%, rgba(12,18,26,0.96) 100%)",
          border: "1px solid rgba(255,255,255,0.16)",
          boxShadow: `0 20px 45px rgba(0,0,0,0.35), 0 0 24px ${accentColor}30`,
          transform: "rotate(-8deg)",
          overflow: "hidden",
        }}
        data-oid="r1pm_ve"
      >
        <div
          style={{
            position: "absolute",
            inset: "10px 12px auto 12px",
            height: "22px",
            borderRadius: "999px",
            background:
              "linear-gradient(90deg, rgba(255,255,255,0.18), rgba(255,255,255,0.05))",
            opacity: 0.65,
          }}
          data-oid="7omhq4:"
        />

        <div
          style={{
            position: "absolute",
            right: "16px",
            top: "18px",
            width: "16px",
            height: "16px",
            borderRadius: "50%",
            background: highlightVisible
              ? accentColor
              : "rgba(255,255,255,0.35)",
            boxShadow: highlightVisible ? `0 0 14px ${accentColor}` : "none",
          }}
          data-oid="rfidom:"
        />

        <div
          style={{
            position: "absolute",
            left: "16px",
            right: "16px",
            bottom: "14px",
            height: "18px",
            borderRadius: "999px",
            background: "rgba(255,255,255,0.06)",
            border: "1px solid rgba(255,255,255,0.08)",
          }}
          data-oid="2oaoi4w"
        />
      </div>

      <div
        style={{
          position: "absolute",
          width: "72px",
          height: "44px",
          borderRadius: "1rem",
          background:
            "linear-gradient(180deg, rgba(255,255,255,0.18) 0%, rgba(255,255,255,0.04) 100%)",
          border: "1px solid rgba(255,255,255,0.14)",
          transform: "translate(12px, -28px) rotate(10deg)",
          boxShadow: `0 0 20px ${accentColor}20`,
        }}
        data-oid="sel9-ez"
      />

      {infoPanelVisible && (
        <div
          style={{
            position: "absolute",
            right: "-10px",
            top: "18px",
            width: "144px",
            padding: "0.75rem 0.85rem",
            borderRadius: "0.9rem",
            background: "rgba(255,255,255,0.08)",
            border: "1px solid rgba(255,255,255,0.12)",
            boxShadow: `0 0 22px ${accentColor}26`,
            opacity: highlightVisible ? 1 : 0,
            transform: highlightVisible ? "translateY(0)" : "translateY(10px)",
            transition: "all 500ms ease",
          }}
          data-oid="5_b45bl"
        >
          <div
            style={{
              fontSize: "0.68rem",
              fontWeight: 700,
              color: "rgba(255,255,255,0.92)",
              marginBottom: "0.25rem",
            }}
            data-oid="u645m84"
          >
            {uiCopy.selectionConfirmed}
          </div>
          <div
            style={{
              fontSize: "0.65rem",
              color: "rgba(255,255,255,0.72)",
              lineHeight: 1.5,
            }}
            data-oid="..10cw2"
          >
            {uiCopy.selectionDetails}
          </div>
        </div>
      )}
    </div>
  );
}

function TouchPoint({
  accentColor,
  active,
  showPulse,
}: {
  accentColor: string;
  active: boolean;
  showPulse: boolean;
}) {
  return (
    <div
      style={{ position: "relative", width: "24px", height: "24px" }}
      data-oid="7luu130"
    >
      {showPulse && (
        <span
          style={{
            position: "absolute",
            inset: 0,
            borderRadius: "50%",
            border: `1px solid ${accentColor}`,
            animation: "tutorialPulse 1.5s ease-out infinite",
          }}
          data-oid="pot9dcl"
        />
      )}
      <span
        style={{
          position: "absolute",
          inset: 0,
          borderRadius: "50%",
          background: active ? accentColor : "rgba(255,255,255,0.3)",
          boxShadow: active ? `0 0 18px ${accentColor}` : "none",
          transition: "all 300ms ease",
        }}
        data-oid="14wqffb"
      />

      <span
        style={{
          position: "absolute",
          inset: "6px",
          borderRadius: "50%",
          background: "rgba(255,255,255,0.95)",
        }}
        data-oid="8y:nnsr"
      />
    </div>
  );
}
